import React, { useEffect, useRef, useCallback } from 'react';
import DNotificationHeader from '@/components/DNotificationHeader';
import ReferralProgram from '@/components/ReferralProgram';
import FeedbackRecordingButton from '@/components/FeedbackRecordingButton';
import { useSessionRecording } from '@/hooks/useSessionRecording';
import RunningJobsDropdown from '@/v3-dashboard/components/RunningJobsDropdown';
import VoiceAgentDropdown from '@/v3-dashboard/components/VoiceAgentDropdown';
import DToastContainer from '@/components/DToast/DToastContainer';
import TeamManagementDropdown from '@/v3-dashboard/components/TeamManagementDropdown';
import DanteLogoIcon from '@/shared/icons/DanteLogoIcon';
import BurgerMenuIcon from '@/shared/icons/BurgerMenuIcon';
import CloseIconMenu from '@/shared/icons/CloseIconMenu';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useParams, useLocation } from 'react-router-dom';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import HeaderAccountMenu from '@/v3-dashboard/components/HeaderAccountMenu';
import RefreshIcon from '@/shared/icons/RefreshIcon';
import LoadingSpinner from '@/shared/icons/LoadingSpinner';
import UpgradeIcon from '@/shared/icons/UpgradeIcon';
import { useJobsData } from '@/hooks/useJobsData';
import { useJobsStore } from '@/stores/chatbot/jobsStore';
import { useUserStore } from '@/stores/user/userStore';
import useModalStore from '@/stores/modal/modalStore';
import * as chatbotService from '@/services/chatbot.service';
import * as voiceService from '@/services/voice.service';
import { GENERAL_POLLING_INTERVAL } from '@/constants';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import useDanteApi from '@/hooks/useDanteApi';
import HeaderLearningHub from '@/v3-dashboard/components/HeaderLearningHub/HeaderLearningHub';
import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';


const TopBar = ({
  isMobileNavOpen,
  setIsMobileNavOpen,
  navContent,
  skipDataFetching = false,
}) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
  const user = useUserStore((state) => state.user);
  const openPlansModal = useModalStore((state) => state.openPlansModal);
  const selectedTeam = useTeamManagementStore((state) => state.selectedTeam);
  const handleCreditsClick = (e) => {
    e?.stopPropagation?.();
    openPlansModal('credits');
  };

  const handleKnowledgebasesClick = (e) => {
    e?.stopPropagation?.();
    openPlansModal('number_of_knowledgebases');
  };

  const isTopTier = ['pro', 'professional', 'business', 'enterprise', 'ultra'].includes(
    String(user?.tier_type || '').toLowerCase()
  );

  const isFeedbackEnabled = true;
  const { getRecordingEvents } = useSessionRecording({ enabled: isFeedbackEnabled });
  const { setJobsData } = useJobsStore();
  const jobsPollingIntervalRef = useRef(null);

  // Get running jobs data from store
  const userJobsData = useJobsData('user');

  // Filter running jobs (not completed, failed, or canceled)
  const runningJobs = userJobsData?.results?.filter(job =>
    job.status !== 'completed' &&
    job.status !== 'failed' &&
    job.status !== 'canceled' &&
    job.status !== 'error'
  ) || [];

  // Check if we're on a voice detail route
  const isVoiceRoute = location.pathname.startsWith('/voice/') &&
                       params?.id &&
                       location.pathname !== '/voice/create';

  // Fetch voice data if on voice route
  const { data: voiceData } = useDanteApi(
    voiceService.getVoice,
    [params.id],
    { skip: !isVoiceRoute },
    params.id
  );

  const hasChatbotSelected = Boolean((selectedChatbot?.kb_id || selectedChatbot?.knowledge_base?.id) && params?.id && !isVoiceRoute);
  const hasVoiceSelected = Boolean(voiceData?.id && params?.id && isVoiceRoute);

  // Poll jobs status for user (all chatbots) - ensures we always have fresh data
  const startJobsPollingPerUser = useCallback(async () => {
    try {
      if (!user?.id) return;

      const response = await chatbotService.getJobsStatusPerUser(user.id);
      setJobsData('user', response.data);
    } catch (error) {
      console.error('Error fetching jobs status:', error);
    }
  }, [user?.id, setJobsData]);

  // Start jobs polling on mount to ensure we catch newly created chatbots training
  useEffect(() => {
    // Skip polling if skipDataFetching flag is set
    if (skipDataFetching) {
      return;
    }

    // Clear any existing interval first
    if (jobsPollingIntervalRef.current) {
      clearInterval(jobsPollingIntervalRef.current);
      jobsPollingIntervalRef.current = null;
    }

    if (user?.id) {
      // Initial fetch
      startJobsPollingPerUser();

      // Set up polling interval
      const interval = setInterval(() => {
        startJobsPollingPerUser();
      }, GENERAL_POLLING_INTERVAL);

      jobsPollingIntervalRef.current = interval;
    }

    // Cleanup function
    return () => {
      if (jobsPollingIntervalRef.current) {
        clearInterval(jobsPollingIntervalRef.current);
        jobsPollingIntervalRef.current = null;
      }
    };
  }, [user?.id, startJobsPollingPerUser, skipDataFetching]);

  // Component to render a single running job in the menu
  const RunningJobCard = ({ job }) => (
    <div className="flex h-8 items-center gap-v3-xs self-stretch">
      <span className="text-v3-h9 font-medium text-v3-black-max">
        {job.knowledge_base_name}
      </span>
      {job.total_discovered_urls > 0 ? (
        <span className="text-v3-h9 font-medium text-v3-black-max ml-auto">
          {(job.urls_completed + job.urls_failed + job.urls_skipped) || 0} / {job.total_discovered_urls}
        </span>
      ) : (
        <LoadingSpinner className="h-3 w-3 text-grey-50 ml-auto" />
      )}
    </div>
  );

  return (
     <header className="flex flex-col lg:flex-row justify-between py-[12px] pl-[28px] pr-v3-lg gap-size2 lg:gap-0 w-full max-w-full h-[60px] bg-v3-glass backdrop-blur-[10px]">
      <DToastContainer hideInMobile />

      <div className="flex items-center justify-end min-w-0 flex-1 w-full">
        <div className="flex items-center justify-between w-full">
             <div className='flex gap-v3-sm items-center flex-shrink-0 '>
             {/* Burger menu button - visible only on tablet/mobile (under 768px) */}
             <button
               onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
               className="md:hidden flex items-center justify-center p-v3-xxs hover:bg-grey-2 rounded-v3-sm transition-colors"
               aria-label="Toggle navigation menu"
             >
               {isMobileNavOpen ? (
                  <IconWrapper size={24}>
                    <CloseIconMenu className="w-full h-full text-v3-black-max" />
                  </IconWrapper>
               ) : (
                  <IconWrapper size={24}>
                    <BurgerMenuIcon className="w-full h-full text-v3-black-max" />
                  </IconWrapper>
               )}
             </button>
             {/* Hide logo and nav items on mobile (under 768px) - only visible on md and above */}
             <div className="hidden md:flex gap-v3-sm items-center">
               <button className='cursor-pointer' onClick={() => window.location.href = '/'}>
                 <DanteLogoIcon/>
               </button>
               {navContent || (
                 <>
                   <TeamManagementDropdown />
                   {hasChatbotSelected && (
                     <>
                       <p className='text-v3-gray-medium'> / </p>
                       <div className="flex items-center justify-center">
                         <RunningJobsDropdown mode="per_user" />
                       </div>
                     </>
                   )}
                   {hasVoiceSelected && (
                     <>
                       <p className='text-v3-gray-medium'> / </p>
                       <div className="flex items-center justify-center">
                         <VoiceAgentDropdown />
                       </div>
                     </>
                   )}
                 </>
               )}
             </div>
         </div>

          <div className='flex items-center  gap-[12px]'>

          {/* Credits and Knowledgebases - clickable to add credits or upgrade (shown for all tiers including free) */}
          {user?.id && (!selectedTeam || selectedTeam?.owner_id === user?.id) && user?.is_demo_account === false && (
            <div className="hidden sm:flex items-center gap-v3-sm">
              <button
                onClick={handleKnowledgebasesClick}
                className={`flex items-center gap-v3-xxs px-v3-xs py-[6px] rounded-v3-sm border border-v3-card-border transition-colors ${
                  isTopTier
                    ? ''
                    : 'hover:bg-v3-menu-hover cursor-pointer'
                }`}
                aria-label="Agents used"
              >
                <span className="text-v3-h10 text-v3-black-secondary">Agents</span>
                <span className="text-v3-h10 font-medium text-v3-black-max">
                  {user?.used_ai_chatbots ?? 0} / {user?.total_ai_chatbots ?? 0}
                </span>
                <IconWrapper size={14}>
                  <UpgradeIcon className="text-v3-purple-primary" />
                </IconWrapper>
              </button>
            </div>
          )}

          <div className="flex items-center">
            <div className="flex items-center gap-v3-ms">
              <div className='flex items-center gap-v3-ms'>
                {/* <ReferralProgram />  */}
                {isFeedbackEnabled && <FeedbackRecordingButton getRecordingEvents={getRecordingEvents} />}
              </div>
              <div className='flex items-center gap-v3-xxs'>
               {/* Running Jobs Refresh Icon with dropdown - only visible when jobs are active */}
               {runningJobs.length > 0 && (
                 <Menu as="div" className="relative">
                   <MenuButton className="relative w-v3-lg h-v3-lg flex items-center justify-center hover:bg-v3-menu-hover rounded-v3-xs cursor-pointer">
                     <IconWrapper size={20}>
                       <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin"/>
                     </IconWrapper>
                     <span className="absolute -top-[6px] -right-[6px] bg-[#ea3529] text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center text-center font-medium">
                       {runningJobs.length}
                     </span>
                   </MenuButton>
                   <MenuItems
                     anchor="bottom end"
                     className="w-[400px] flex flex-col items-start rounded-v3-sm border border-v3-card-border bg-white shadow-v3-card max-h-64 overflow-y-auto z-50 focus:outline-none transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] data-[closed]:scale-95 data-[closed]:opacity-0 mt-1"
                   >
                     <div className="flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm h-12">
                       <h3 className="text-v3-h9 font-bold text-v3-black-secondary">Currently training</h3>
                     </div>
                     <div className="flex flex-col w-full">
                       {runningJobs.map((job) => (
                         <MenuItem key={job.job_id} as="div" className="flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm hover:bg-v3-menu-hover cursor-pointer" onClick={() => {
                           navigate(`/training/${job.job_id}`);
                         }}>
                           <RunningJobCard job={job} />
                         </MenuItem>
                       ))}
                     </div>
                   </MenuItems>
                 </Menu>
               )}
               <HeaderLearningHub />
               <DNotificationHeader skipDataFetching={skipDataFetching} />
                </div>
            </div>
          </div>

          <div className="">
            <HeaderAccountMenu />
          </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopBar